/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionInvokeUIScript';

const ActionInvokeUIScriptManager: ActionManagerFn<'ActionInvokeUIScript'> = (
  platform: Platform,
): ActionManager<'ActionInvokeUIScript'> => {
  return {
    ...baseActionManager(platform, 'ActionInvokeUIScript'),
    isChildAllowed: (page, item, childType) =>
      ['ActionSetParam'].includes(childType),
    defaultValue,
    props: {
      ...baseActionProps,
      scriptName: {
        label: 'UI Script Name',
        type: 'string',
        required: true,
      },
    },
    name: 'Invoke UI Script',
    icon: { iconName: 'laptop-file' },
    defaultChildren: ['ActionSetParam', 'ActionSetParam'],
    description: `**Overview**<br/>
The Invoke UI Script action is a client-side logic component designed to execute a named "UI Script" registered within the application environment. Unlike Controller Functions, which are typically bound to a specific page's context, UI Scripts often represent shared or global utility logic that can be reused across different parts of the application.<br/><br/>
This component functions as a wrapper for the script execution. It strictly relies on ActionSetParam child components to define the arguments or context variables required by the script. This separation allows the same generic script to be called with different parameters depending on where it is invoked.

**Key Features**<br/>
* **Named Execution:** References logic via a simple string property (scriptName), allowing it to call standard or globally defined scripts.
* **Parameter Injection:** Uses ActionSetParam child actions to construct the input object passed to the script, ensuring modular and reusable code execution.
* **Strict Child Hierarchy:** Explicitly limits children to ActionSetParam (isChildAllowed), enforcing a clean structure dedicated solely to argument definition.

**Properties**<br/>
* **UI Script Name (Required):** A string property identifying the specific script to execute.
  * *Function:* The system looks up the script by this name in the registry and runs it.

**Use Cases**<br/>
* **Shared Utilities:** Calling a global FormatCurrency or CalculateTax script that is used on multiple different pages/forms.
* **Third-Party Wrappers:** Invoking a wrapper script that initializes or interacts with a generic external library (e.g., a charting or mapping tool) configured globally for the app.

**Next Step**<br/>
Would you like to examine the Action Loop Deselect component to see how to handle selection states iteratively?`,
  };
};

export default ActionInvokeUIScriptManager;
